import {
  Alert,
  AlertIcon,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useForm } from 'react-hook-form';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';

interface RejectReenrolmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  taskId: string;
  proposedEnrolmentId: number | null;
  proposedCourseName: string | null;
}

interface RejectReenrolmentFormData {
  reason: string;
}

export function RejectReenrolmentModal({
  isOpen,
  onClose,
  taskId,
  proposedEnrolmentId,
  proposedCourseName,
}: RejectReenrolmentModalProps) {
  const utils = trpcAdmin.useUtils();
  const toast = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<RejectReenrolmentFormData>({
    defaultValues: {
      reason: '',
    },
  });

  const rejectMutation = trpcAdmin.support.task.rejectReenrolment.useMutation({
    onSuccess: () => {
      toast({
        status: 'success',
        title: t`Re-enrolment rejected`,
      });
      void utils.support.task.get.invalidate();
      void utils.support.task.list.invalidate(undefined, { refetchType: 'active' });
      void utils.students.enrollmentsById.invalidate();
      reset();
      onClose();
    },
    onError: (error) => {
      toast({
        status: 'error',
        title: t`Failed to reject re-enrolment`,
        description: error.message,
      });
    },
  });

  const onSubmit = (data: RejectReenrolmentFormData) => {
    if (proposedEnrolmentId == null) {
      return;
    }

    rejectMutation.mutate({
      taskId,
      proposedEnrolmentId,
      reason: data.reason,
    });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <ConfirmationDialog
      isOpen={isOpen}
      onClose={handleClose}
      title={t`Reject Re-enrolment`}
      body={
        <VStack align="stretch" spacing={4}>
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            <Text fontSize="sm">
              <Trans>
                This will delete the proposed enrolment
                {proposedCourseName != null ? ` (${proposedCourseName})` : ''} and keep the current
                enrolment as-is.
              </Trans>
            </Text>
          </Alert>

          <FormControl isRequired isInvalid={!!errors.reason}>
            <FormLabel>
              <Trans>Reason for rejection</Trans>
            </FormLabel>
            <Textarea
              {...register('reason', {
                required: t`Please provide a reason for rejection`,
              })}
              placeholder={t`Explain why this re-enrolment is being rejected`}
              rows={3}
            />
            {errors.reason ? <FormErrorMessage>{errors.reason.message}</FormErrorMessage> : null}
          </FormControl>
        </VStack>
      }
      onConfirm={handleSubmit(onSubmit)}
      confirmLabel={t`Reject Re-enrolment`}
      isLoading={rejectMutation.isPending}
      variant="destructive"
    />
  );
}
